"use client";

import { Icon, type IconName } from "@/components/ui/icon";
import { FilterVariantCount } from "@/components/ui/filters";
import type { VariantFilterValue } from "@/lib/api/types";
import { formatEntityForms, type VariantField } from "@/lib/search/entity-variants";
import { resolveItemScope, type FilterScope } from "@/features/search/lib/filter-scope";
import { cn } from "@/lib/utils";
import { FilterValueActions } from "../actions";
import { ScopeDropdown } from "./internal/scope-dropdown";
import { VariantsDropdown } from "./internal/variants-dropdown";

export function FilterScopedValueInput({
  value,
  label,
  icon,
  fallbackScope,
  variantsField,
  onChange,
  onRemove,
}: {
  value: VariantFilterValue;
  label: string;
  icon?: IconName;
  /**
   * Section-level scope the backend applies when the item has no `scope`
   * (e.g. `industry_filter_type`), so legacy searches render what they match.
   */
  fallbackScope?: FilterScope | null;
  /** Opt-in: lets the chip carry alias variants matched from this field. */
  variantsField?: VariantField;
  onChange: (updated: VariantFilterValue) => void;
  onRemove: () => void;
}) {
  const isExcluded = value.mode === "exclude";
  const scope = resolveItemScope(value.scope, fallbackScope);

  return (
    <div
      className={cn(
        "border rounded-md overflow-clip",
        isExcluded ? "border-destructive/30" : "border-border",
      )}
    >
      <div className="flex items-center justify-between bg-contrast/4 h-[34px] px-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {icon && (
            <Icon
              name={icon}
              size={13}
              className={cn(
                "shrink-0",
                isExcluded
                  ? "text-destructive dark:text-red-400 opacity-60"
                  : "text-contrast opacity-60",
              )}
            />
          )}
          <span
            title={value.variants?.length ? formatEntityForms({ text: label, variants: value.variants }) : undefined}
            className={cn(
              "text-[13px] font-normal truncate tracking-[-0.13px]",
              isExcluded
                ? "text-destructive/80 dark:text-red-400/80 line-through"
                : "text-foreground/80",
            )}
          >
            {label}
            <FilterVariantCount count={value.variants?.length ?? 0} />
          </span>
        </div>
        <FilterValueActions
          excluded={isExcluded}
          required={value.required ?? false}
          onToggleRequired={() => onChange({ ...value, required: !value.required })}
          onToggleExclude={() =>
            onChange({ ...value, mode: isExcluded ? "include" : "exclude" })
          }
          onRemove={onRemove}
        />
      </div>
      <div className="flex items-center justify-end gap-3 bg-contrast/8 border-t border-border h-7 px-2">
        {variantsField && (
          <VariantsDropdown field={variantsField} item={value} onChange={onChange} />
        )}
        <ScopeDropdown value={scope} onChange={(v) => onChange({ ...value, scope: v })} />
      </div>
    </div>
  );
}
